import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Header } from '@/components/layout/Header';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Modal } from '@/components/ui/Modal';
import { Card } from '@/components/ui/Card';
import { api } from '@/utils/api';

interface Organization {
    id: string;
    name: string;
    description?: string;
}

interface OIDCProvider {
    id: string;
    providerType: string;
    providerName: string;
    enabled: boolean;
}

export function OrgSettings() {
    const { orgId } = useParams<{ orgId: string }>();
    const [, setOrg] = useState<Organization | null>(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(true);
    const [oidcProviders] = useState<OIDCProvider[]>([]);
    const [isOidcModalOpen, setIsOidcModalOpen] = useState(false);
    const [newProvider, setNewProvider] = useState({
        providerType: 'google',
        providerName: '',
        clientId: '',
        clientSecret: '',
    });

    useEffect(() => {
        loadOrg();
        loadOidcProviders();
    }, [orgId]);

    const loadOrg = async () => {
        if (!orgId) return;

        try {
            const data = await api.getOrganization(orgId);
            setOrg(data);
            setName(data.name);
            setDescription(data.description || '');
        } catch (error) {
            console.error('Failed to load organization:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadOidcProviders = async () => {
        if (!orgId) return;

        try {
            // TODO: Add API endpoint for org OIDC providers
            // const providers = await api.getOrgOidcProviders(orgId);
            // setOidcProviders(providers);
        } catch (error) {
            console.error('Failed to load OIDC providers:', error);
        }
    };

    const handleSave = async () => {
        if (!orgId) return;

        try {
            await api.updateOrganization(orgId, { name, description });
            await loadOrg();
            alert('Organization updated successfully');
        } catch (error) {
            console.error('Failed to update organization:', error);
            alert('Failed to update organization');
        }
    };

    const handleAddOidcProvider = async () => {
        if (!orgId) return;

        try {
            // TODO: Add API endpoint
            // await api.createOrgOidcProvider(orgId, newProvider);
            setIsOidcModalOpen(false);
            setNewProvider({
                providerType: 'google',
                providerName: '',
                clientId: '',
                clientSecret: '',
            });
            await loadOidcProviders();
            alert('OIDC provider added');
        } catch (error) {
            console.error('Failed to add OIDC provider:', error);
            alert('Failed to add OIDC provider');
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
                <Header />
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Loading...
                </div>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <Header />

            <main style={{ flex: 1, overflow: 'auto', padding: 'var(--spacing-2xl)' }}>
                <div className="container" style={{ maxWidth: '800px' }}>
                    <h1 style={{ marginBottom: 'var(--spacing-xl)' }}>Organization Settings</h1>

                    {/* Basic Settings */}
                    <section style={{ marginBottom: 'var(--spacing-2xl)' }}>
                        <h2 style={{ marginBottom: 'var(--spacing-lg)' }}>Basic Information</h2>
                        <div className="panel">
                            <div className="flex flex-col gap-lg">
                                <Input
                                    label="Organization Name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                />
                                <div className="flex flex-col gap-sm">
                                    <label className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>
                                        Description
                                    </label>
                                    <textarea
                                        value={description}
                                        onChange={(e) => setDescription(e.target.value)}
                                        placeholder="Brief description..."
                                        rows={4}
                                    />
                                </div>
                                <div>
                                    <Button variant="primary" onClick={handleSave}>
                                        Save Changes
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* OIDC Providers Section */}
                    <section>
                        <div className="flex justify-between items-center" style={{ marginBottom: 'var(--spacing-lg)' }}>
                            <h2 style={{ margin: 0 }}>SSO / OIDC Providers</h2>
                            <Button variant="primary" onClick={() => setIsOidcModalOpen(true)}>
                                + Add Provider
                            </Button>
                        </div>

                        <div className="panel">
                            <p style={{ marginBottom: 'var(--spacing-lg)', color: 'var(--color-text-secondary)' }}>
                                Configure OIDC/SSO providers for your organization. Members can sign in using these providers.
                            </p>

                            {oidcProviders.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: 'var(--spacing-xl)', color: 'var(--color-text-tertiary)' }}>
                                    No OIDC providers configured. Add one to enable SSO for your organization.
                                </div>
                            ) : (
                                <div className="flex flex-col gap-md">
                                    {oidcProviders.map(provider => (
                                        <Card key={provider.id}>
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <div style={{ fontWeight: 500, marginBottom: 'var(--spacing-xs)' }}>
                                                        {provider.providerName}
                                                    </div>
                                                    <div style={{ fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                                                        Type: {provider.providerType.charAt(0).toUpperCase() + provider.providerType.slice(1)}
                                                    </div>
                                                    <div style={{ fontSize: '0.75rem', color: provider.enabled ? 'var(--color-success)' : 'var(--color-text-tertiary)' }}>
                                                        {provider.enabled ? '● Enabled' : '○ Disabled'}
                                                    </div>
                                                </div>
                                                <div className="flex gap-sm">
                                                    <Button variant="ghost">Edit</Button>
                                                    <Button variant="ghost" style={{ color: 'var(--color-error)' }}>
                                                        Delete
                                                    </Button>
                                                </div>
                                            </div>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>
                </div>
            </main>

            {/* Add OIDC Provider Modal */}
            <Modal
                open={isOidcModalOpen}
                onClose={() => setIsOidcModalOpen(false)}
                title="Add OIDC Provider"
            >
                <div className="flex flex-col gap-lg">
                    <Select
                        value={newProvider.providerType}
                        onValueChange={(value) => setNewProvider({ ...newProvider, providerType: value })}
                        options={[
                            { value: 'google', label: 'Google Workspace' },
                            { value: 'github', label: 'GitHub' },
                            { value: 'custom', label: 'Custom OIDC' },
                        ]}
                        label="Provider Type"
                    />

                    <Input
                        label="Provider Name"
                        value={newProvider.providerName}
                        onChange={(e) => setNewProvider({ ...newProvider, providerName: e.target.value })}
                        placeholder="e.g., Company Google SSO"
                    />

                    <Input
                        label="Client ID"
                        value={newProvider.clientId}
                        onChange={(e) => setNewProvider({ ...newProvider, clientId: e.target.value })}
                    />

                    <Input
                        label="Client Secret"
                        type="password"
                        value={newProvider.clientSecret}
                        onChange={(e) => setNewProvider({ ...newProvider, clientSecret: e.target.value })}
                    />

                    {newProvider.providerType === 'custom' && (
                        <>
                            <Input label="Issuer URL" placeholder="https://accounts.example.com" />
                            <Input label="Authorization Endpoint" placeholder="https://accounts.example.com/authorize" />
                            <Input label="Token Endpoint" placeholder="https://accounts.example.com/token" />
                            <Input label="Userinfo Endpoint" placeholder="https://accounts.example.com/userinfo" />
                        </>
                    )}

                    <div className="flex gap-md" style={{ justifyContent: 'flex-end', marginTop: 'var(--spacing-md)' }}>
                        <Button variant="ghost" onClick={() => setIsOidcModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleAddOidcProvider}
                            disabled={!newProvider.providerName || !newProvider.clientId || !newProvider.clientSecret}
                        >
                            Add Provider
                        </Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
}
